/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ToolbarButton } from '@ckeditor/ckeditor5-metadata';
import { Box, Checkbox, Divider, Radio, type SxProps, Typography } from '@mui/material';
import { type ReactNode } from 'react';

import { premiumPluginTooltip } from '../../../../constants/tooltips';
import { HighlightSearch } from '../../../HighlightSearch/HighlightSearch';
import { PluginPremiumBadge, PluginDisabledBadge, PluginConflictBadge, PluginDependsBadge } from '../../../PluginBadges/PluginBadges';
import type { FeatureItemProps } from '../types';
import { removeDuplicateStrings } from '../../../../utils/removeDuplicateStrings/removeDuplicateStrings';
import { UiComponent } from '../UiComponent/UiComponent';

const blackListedIcons = [
	'imageStyle:side'
];

export function FeatureItem( {
	feature,
	isLast,
	onPluginSelectionChange,
	isSingleFeatureChoice,
	toolbarOptions
}: FeatureItemProps ): ReactNode {
	const inputProps = {
		id: `group-${ feature.id }`,
		value: feature.name,
		checked: feature.isSelected,
		disabled: feature.isDisabled || feature.isVirtual,
		onChange: () => onPluginSelectionChange( feature ),
		'data-testid': 'toggle-feature-input'
	};

	// To avoid rendering the same UI components twice, let's create a list of buttons to render.
	const uiComponentsToRender = new Set(
		feature.plugins.flatMap( plugin => {
			return plugin.uiComponents
				.filter( ( component ): component is ToolbarButton => {
					return component.type === 'Button' || component.type === 'SplitButton';
				} )
				.map( component => component.name );
		} )
	);

	// Then, filter out the duplicated items.
	const uiComponentList = feature.plugins.flatMap( plugin => {
		return plugin.uiComponents
			.filter( ( component ): component is ToolbarButton => {
				return component.type === 'Button' || component.type === 'SplitButton';
			} )
			.filter( component => {
				const shouldRender = uiComponentsToRender.has( component.name );

				uiComponentsToRender.delete( component.name );

				return shouldRender;
			} )
			.filter( component => !blackListedIcons.includes( component.name ) );
	} );

	const unsupportedEditorTypes = removeDuplicateStrings( feature.plugins.flatMap( p => p.unsupportedEditorTypes ) );
	const opacity = feature.isDisabled ? 0.5 : 1;
	const cursor = feature.isDisabled ? 'default' : 'pointer';

	return (
		<div>
			<Box sx={ {
				padding: '10px',
				paddingLeft: '20px',
				display: 'flex',
				alignItems: 'flex-start',
				justifyContent: 'space-between',
				gap: '5px'
			} }
			>
				<Box sx={ { display: 'flex', flexDirection: 'column' } }>
					<Box sx={ { display: 'flex', gap: '5px' } }>
						<HeaderElement labelFor={ inputProps.id.replace( / /g, '' ) } sx={ { opacity, cursor } }>
							<HighlightSearch>
								{ feature.name }
							</HighlightSearch>
						</HeaderElement>
						{ uiComponentList.length > 0 && (
							<Box sx={ { height: '20px', minWidth: '20px', overflow: 'hidden' } }>
								{ uiComponentList.slice( 0, 5 ).map( component => (
									<UiComponent
										key={ component.name }
										component={ component }
										opacity={ opacity }
										toolbarOptions={ toolbarOptions }
									/>
								) ) }
							</Box>
						) }
						{
							feature.dependantFeatures.length > 0 &&
							<PluginDependsBadge
								tooltipTitle={ 'This feature depends on following features:' }
								tooltipList={ feature.dependantFeatures }
							/>
						}
						{ feature.isPremium && <PluginPremiumBadge onlyIcon tooltipTitle={ premiumPluginTooltip }/> }
						{ feature.isDisabled && <PluginDisabledBadge tooltipList={ unsupportedEditorTypes }/> }
						{ feature.hasConflict && !feature.isDisabled && <PluginConflictBadge tooltipList={ feature.conflictingFeatures }/> }
					</Box>
					<DescriptionElement sx={ { opacity } }>
						<HighlightSearch>
							{ feature.description }
						</HighlightSearch>
					</DescriptionElement>
				</Box>
				{ !isSingleFeatureChoice && ( <Checkbox { ...inputProps } indeterminate={ feature.isSelectedIndeterminate }/> ) }
				{ isSingleFeatureChoice && ( <Radio { ...inputProps } /> ) }
			</Box>
			{ !isLast && <Divider sx={ { margin: '0 10px' } } data-testid='divider'/> }
		</div>
	);
}

function HeaderElement( { children, labelFor, sx = {} }: { children: ReactNode; labelFor: string; sx?: SxProps } ) {
	return (
		<Typography
			htmlFor={ labelFor }
			component='label'
			fontSize={ '12px' }
			lineHeight={ '20px' }
			fontWeight={ 500 }
			color={ 'text.primary' }
			sx={ sx }
		>
			{ children }
		</Typography>
	);
}

function DescriptionElement( { children, sx = {} }: { children: ReactNode; sx?: SxProps } ) {
	return (
		<Typography
			fontSize={ '12px' }
			lineHeight={ '20px' }
			fontWeight={ 400 }
			color={ 'text.secondary' }
			sx={ { padding: 0, ...sx } }
		>
			{ children }
		</Typography>
	);
}
